/**
 * Reward Amount Mixin
 *
 * Provides shared functionality for calculating and formatting reward amounts,
 * including point conversion logic for non-cash awards.
 *
 * Usage:
 * import rewardAmountMixin from "@/mixins/rewardAmountMixin";
 *
 * export default {
 *   mixins: [rewardAmountMixin],
 *   // Component must have props: budgets, budgetId, cashAward
 *   // Component must use mapState for userProfile
 * }
 *
 * Methods provided:
 * - getBudget(budgetId) - Finds a budget by ID from budgets array
 * - calculatePresetAmount(amount, budget) - Calculates amount with point conversion if applicable
 * - formatRewardAmount(amount) - Formats reward amount with point conversion and units
 */

import { mapState } from "vuex";
import currency from "currency.js";
import formatCurrencyMixin from "@/mixins/formatCurrency";

export default {
  mixins: [formatCurrencyMixin],
  computed: {
    ...mapState(["userProfile"]),
    selectedBudget() {
      if (!this.budgetId || !this.budgets || !this.budgets.length) return null;
      return this.budgets.find((budget) => budget.budgetId === this.budgetId);
    },
  },
  methods: {
    /**
     * Finds a budget by budgetId from the budgets array
     * @param {Number} budgetId - The budget ID to find
     * @returns {Object|null} - The budget object or null if not found
     */
    getBudget(budgetId) {
      if (!budgetId || !this.budgets || !this.budgets.length) return null;
      return this.budgets.find((b) => b.budgetId === budgetId);
    },

    /**
     * Calculates preset amount with point conversion if applicable
     * @param {Number} amt - The base amount
     * @param {Object} budget - The budget object (optional)
     * @param {Boolean} ignorePointRatio - Whether to ignore the point ratio for custom amounts
     * @returns {Object} - Currency.js object with converted amount
     */
    calculatePresetAmount(amt, budget, ignorePointRatio = false) {
      let amount = currency(amt, {
        symbol: this.cashAward ? "$" : "",
      });
      if (
        !ignorePointRatio &&
        this.userProfile &&
        this.userProfile.Client &&
        this.userProfile.Client.pointRatio &&
        budget &&
        budget.Award &&
        budget.Award.cashValuePoints
      ) {
        amount = amount.multiply(this.userProfile.Client.pointRatio);
      }
      return amount;
    },

    /**
     * Formats reward amount with point conversion and units
     * This is a convenience method that combines calculatePresetAmount and formatting
     * @param {Number} amount - The reward amount to format
     * @param {Boolean} ignorePointRatio - Whether to ignore the point ratio for custom amounts
     * @returns {String} - Formatted string with converted amount and units
     */
    formatRewardAmount(amount, ignorePointRatio = false) {
      const budget = this.selectedBudget;
      const calculatedAmount = this.calculatePresetAmount(amount, budget, ignorePointRatio);
      return this.formatCurrency(calculatedAmount, budget?.Award, true);
    },
  },
};
